import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { preContagemBalancoService } from '../services/api';
import '../styles/PreContagemBalanco.css';

const pad = (value) => String(value).padStart(2, '0');

const formatDateTime = (iso) => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '-';
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const normalizeAddress = (value) => String(value || '')
  .trim()
  .toUpperCase()
  .replace(/\s+/g, '')
  .replace(/-\d+$/, '');

const isAddressCode = (value) => /^[A-Za-z]/.test(String(value || '').trim());

const sanitizeQuantity = (value) => {
  const parsed = parseInt(String(value || '').trim(), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const splitAddressList = (value) => String(value || '')
  .split(';')
  .map((item) => item.trim())
  .filter(Boolean);

const parseAddressInfo = (value, fallbackBase = '') => {
  const enderecoCompleto = String(value || '').trim().toUpperCase();
  if (!enderecoCompleto) return null;
  const match = enderecoCompleto.match(/^(.*)-(\d+)$/);
  const enderecoBase = normalizeAddress(match ? match[1] : fallbackBase || enderecoCompleto);
  const enderecoSequencia = match ? parseInt(match[2], 10) || 1 : 1;
  return { enderecoCompleto, enderecoBase, enderecoSequencia };
};

const getAddressInfos = (item) => {
  const addresses = [
    ...(Array.isArray(item?.enderecosCompletos) ? item.enderecosCompletos : []),
    ...splitAddressList(item?.enderecoCompleto)
  ];
  const unique = new Map();
  addresses.forEach((address) => {
    const parsed = parseAddressInfo(address, item?.enderecoBase);
    if (parsed) unique.set(parsed.enderecoCompleto, parsed);
  });
  return [...unique.values()].sort((a, b) => a.enderecoSequencia - b.enderecoSequencia);
};

const formatAddressInfos = (infos) => infos.map((item) => item.enderecoCompleto).join('; ');

const mergeEntries = (items) => {
  const grouped = new Map();

  items.forEach((item) => {
    const key = String(item?.cdprodu || item?.barras || item?.rawCode || '').trim();
    if (!key) return;

    const current = grouped.get(key);
    const infos = getAddressInfos(item);

    if (!current) {
      grouped.set(key, {
        ...item,
        quantidade: sanitizeQuantity(item.quantidade),
        enderecosCompletos: infos.map((info) => info.enderecoCompleto),
        enderecoCompleto: formatAddressInfos(infos),
        enderecoBase: infos[0]?.enderecoBase || normalizeAddress(item.enderecoBase),
        enderecoSequencia: infos[infos.length - 1]?.enderecoSequencia || item.enderecoSequencia || 1
      });
      return;
    }

    const infoMap = new Map(getAddressInfos(current).map((info) => [info.enderecoCompleto, info]));
    infos.forEach((info) => infoMap.set(info.enderecoCompleto, info));
    const mergedInfos = [...infoMap.values()].sort((a, b) => a.enderecoSequencia - b.enderecoSequencia);
    const currentCreated = new Date(current.createdAt || 0).getTime();
    const itemCreated = new Date(item.createdAt || 0).getTime();
    const newest = itemCreated >= currentCreated ? item : current;

    grouped.set(key, {
      ...current,
      ...newest,
      quantidade: sanitizeQuantity(current.quantidade) + sanitizeQuantity(item.quantidade),
      barras: current.barras || item.barras || '',
      rawCode: item.rawCode || current.rawCode,
      estoqueFilial: item.estoqueFilial ?? current.estoqueFilial ?? null,
      enderecoSistema: item.enderecoSistema || current.enderecoSistema || '',
      enderecosCompletos: mergedInfos.map((info) => info.enderecoCompleto),
      enderecoCompleto: formatAddressInfos(mergedInfos),
      enderecoBase: mergedInfos[0]?.enderecoBase || current.enderecoBase || normalizeAddress(item.enderecoBase),
      enderecoSequencia: mergedInfos[mergedInfos.length - 1]?.enderecoSequencia || current.enderecoSequencia || 1,
      createdAt: newest.createdAt || current.createdAt
    });
  });

  return [...grouped.values()].sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
};

const getNextAddressForBase = (entries, enderecoBase) => {
  const base = normalizeAddress(enderecoBase);
  const sequences = entries
    .flatMap((item) => getAddressInfos(item))
    .filter((info) => info.enderecoBase === base)
    .map((info) => info.enderecoSequencia);
  return `${base}-${(sequences.length ? Math.max(...sequences) : 0) + 1}`;
};

const buildStorageKey = (userData) => {
  const userId = userData?.id || userData?.matricula || userData?.nome || 'anonimo';
  const filial = userData?.loja_id || userData?.lojaId || userData?.loja?.id || 'sem-loja';
  return `pre-contagem-balanco:${userId}:${filial}`;
};

const buildMetaStorageKey = (storageKey) => `${storageKey}:meta`;

const loadEntries = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? mergeEntries(parsed) : [];
  } catch {
    return [];
  }
};

const loadMeta = (metaStorageKey, userData) => {
  try {
    const raw = localStorage.getItem(metaStorageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      matriculaContador: String(parsed?.matriculaContador || userData?.matricula || userData?.cdfun || userData?.CDFUN || '')
    };
  } catch {
    return {
      matriculaContador: String(userData?.matricula || userData?.cdfun || userData?.CDFUN || '')
    };
  }
};

const pickBarcode = (produto, rawCode) => {
  const candidates = [
    produto?.BARRA,
    produto?.BAR1,
    produto?.BAR2,
    produto?.BAR3,
    produto?.BAR4,
    rawCode
  ].map((item) => String(item || '').trim()).filter(Boolean);

  return candidates[0] || '';
};

const buildExportRows = (entries, matriculaContador) => [...entries]
  .slice()
  .reverse()
  .map((item) => ({
    MATRICULA: String(matriculaContador || '').trim(),
    CODINT: item.cdprodu,
    BARRAS: item.barras || '',
    NOME: item.nome,
    QTD: sanitizeQuantity(item.quantidade),
    ENDERECO: item.enderecoCompleto
  }));

const csvEscape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const downloadBlob = (filename, content, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const splitCsvLine = (line, delimiter) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values.map((value) => value.trim());
};

const parseCsvText = (text) => {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length) return [];

  const delimiter = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ';' : ',';
  const headers = splitCsvLine(lines[0], delimiter).map((header) => header.toUpperCase());

  return lines.slice(1).map((line) => {
    const values = splitCsvLine(line, delimiter);
    return headers.reduce((acc, header, index) => {
      acc[header] = values[index] ?? '';
      return acc;
    }, {});
  });
};

const PreContagemBalanco = ({ userData }) => {
  const produtoInputRef = useRef(null);
  const quantidadeInputRef = useRef(null);
  const enderecoInputRef = useRef(null);
  const importInputRef = useRef(null);
  const processingRef = useRef(false);
  const storageKey = useMemo(() => buildStorageKey(userData), [userData]);
  const metaStorageKey = useMemo(() => buildMetaStorageKey(storageKey), [storageKey]);

  const [produtoLido, setProdutoLido] = useState('');
  const [quantidade, setQuantidade] = useState('1');
  const [endereco, setEndereco] = useState('');
  const [matriculaContador, setMatriculaContador] = useState('');
  const [entries, setEntries] = useState([]);
  const [queue, setQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [ultimoRegistro, setUltimoRegistro] = useState(null);
  const [showDivergencias, setShowDivergencias] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const focusProduto = useCallback(() => {
    window.requestAnimationFrame(() => {
      produtoInputRef.current?.focus();
      produtoInputRef.current?.select?.();
    });
  }, []);

  const mostrarFeedback = useCallback((type, message) => {
    setFeedback({
      id: Date.now(),
      type,
      message
    });
  }, []);

  useEffect(() => {
    setEntries(loadEntries(storageKey));
  }, [storageKey]);

  useEffect(() => {
    const meta = loadMeta(metaStorageKey, userData);
    setMatriculaContador(meta.matriculaContador);
  }, [metaStorageKey, userData]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(entries));
  }, [entries, storageKey]);

  useEffect(() => {
    localStorage.setItem(metaStorageKey, JSON.stringify({ matriculaContador: String(matriculaContador || '').trim() }));
  }, [matriculaContador, metaStorageKey]);

  useEffect(() => {
    setUltimoRegistro(entries[0] || null);
  }, [entries]);

  useEffect(() => {
    focusProduto();
  }, [focusProduto]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = window.setTimeout(() => setFeedback(null), 3200);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (processingRef.current || queue.length === 0) return undefined;

    const currentJob = queue[0];
    let cancelled = false;

    const processar = async () => {
      processingRef.current = true;
      setIsProcessing(true);
      try {
        const produto = await preContagemBalancoService.buscarProduto(
          currentJob.rawCode,
          userData?.loja_id || userData?.lojaId || userData?.loja?.id || 22
        );
        if (cancelled) return;

        if (!produto?.CDPRODU) {
          throw new Error(`Produto não encontrado para o código ${currentJob.rawCode}`);
        }

        let novoRegistro = null;
        setEntries((prev) => {
          const cdproduAtual = String(produto.CDPRODU);
          const anterior = prev[0];
          const existente = prev.find((item) => String(item.cdprodu) === cdproduAtual);
          const infosExistentes = existente ? getAddressInfos(existente) : [];
          const ultimoEnderecoMesmoProdutoMesmoBase = anterior
            && String(anterior.cdprodu) === cdproduAtual
            && normalizeAddress(currentJob.enderecoBase) === normalizeAddress(anterior.enderecoBase)
            ? infosExistentes.filter((info) => info.enderecoBase === normalizeAddress(currentJob.enderecoBase)).slice(-1)[0]?.enderecoCompleto
            : null;
          const enderecoCompleto = ultimoEnderecoMesmoProdutoMesmoBase || getNextAddressForBase(prev, currentJob.enderecoBase);
          novoRegistro = {
            ...(existente || {}),
            id: existente?.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            createdAt: new Date().toISOString(),
            rawCode: currentJob.rawCode || existente?.rawCode || '',
            barras: existente?.barras || pickBarcode(produto, currentJob.rawCode),
            quantidade: (existente ? sanitizeQuantity(existente.quantidade) : 0) + sanitizeQuantity(currentJob.quantidade),
            enderecoBase: currentJob.enderecoBase,
            enderecoSequencia: parseAddressInfo(enderecoCompleto, currentJob.enderecoBase)?.enderecoSequencia || 1,
            enderecoCompleto,
            enderecosCompletos: [
              ...(existente?.enderecosCompletos || splitAddressList(existente?.enderecoCompleto)),
              enderecoCompleto
            ],
            cdprodu: String(produto.CDPRODU),
            nome: produto.NOME || existente?.nome || 'Produto sem descrição',
            estoqueFilial: produto.ESTOQUE_FILIAL ?? existente?.estoqueFilial ?? null,
            enderecoSistema: produto.ENDERECO_SISTEMA || existente?.enderecoSistema || ''
          };
          return mergeEntries([novoRegistro, ...prev.filter((item) => String(item.cdprodu) !== cdproduAtual)]);
        });

        if (novoRegistro) {
          setUltimoRegistro(novoRegistro);
          const estoqueMsg = novoRegistro.estoqueFilial === null || novoRegistro.estoqueFilial === undefined
            ? ''
            : ` Estoque filial: ${novoRegistro.estoqueFilial}.`;
          const ultimoEndereco = splitAddressList(novoRegistro.enderecoCompleto).slice(-1)[0] || novoRegistro.enderecoCompleto;
          mostrarFeedback('success', `${novoRegistro.cdprodu} - ${novoRegistro.nome} registrado em ${ultimoEndereco}.${estoqueMsg}`);
        }
      } catch (error) {
        if (!cancelled) {
          mostrarFeedback('error', error?.message || `Falha ao localizar o código ${currentJob.rawCode}.`);
        }
      } finally {
        processingRef.current = false;
        if (!cancelled) {
          setQueue((prev) => prev.slice(1));
          setIsProcessing(false);
          focusProduto();
        }
      }
    };

    processar();

    return () => {
      cancelled = true;
    };
  }, [focusProduto, mostrarFeedback, queue, userData]);

  const enderecoAtual = useMemo(() => normalizeAddress(endereco), [endereco]);

  const proximoEndereco = useMemo(() => {
    if (!enderecoAtual) return '-';
    const registrados = entries
      .flatMap((item) => getAddressInfos(item))
      .filter((info) => info.enderecoBase === enderecoAtual)
      .length;
    const pendentes = queue.filter((item) => item.enderecoBase === enderecoAtual).length;
    return `${enderecoAtual}-${registrados + pendentes + 1}`;
  }, [enderecoAtual, entries, queue]);

  const resumo = useMemo(() => {
    const totalRegistros = entries.length;
    const totalUnidades = entries.reduce((acc, item) => acc + sanitizeQuantity(item.quantidade), 0);
    const totalEnderecos = new Set(entries.map((item) => item.enderecoBase)).size;
    return {
      totalRegistros,
      totalUnidades,
      totalEnderecos
    };
  }, [entries]);

  const exportRows = useMemo(
    () => buildExportRows(entries, matriculaContador),
    [entries, matriculaContador]
  );
  const divergencias = useMemo(() => {
    const grouped = new Map();

    entries.forEach((item) => {
      const key = String(item.cdprodu || item.barras || item.rawCode || '');
      if (!key) return;

      const prev = grouped.get(key) || {
        MATRICULA: String(matriculaContador || '').trim(),
        CODINT: String(item.cdprodu || ''),
        BARRAS: item.barras || '',
        NOME: item.nome || 'Produto sem descrição',
        QTD_CONTADA: 0,
        ESTOQUE_FILIAL: item.estoqueFilial ?? null,
        ENDERECOS: new Set()
      };

      prev.QTD_CONTADA += sanitizeQuantity(item.quantidade);
      if ((prev.ESTOQUE_FILIAL === null || prev.ESTOQUE_FILIAL === undefined) && item.estoqueFilial !== null && item.estoqueFilial !== undefined) {
        prev.ESTOQUE_FILIAL = item.estoqueFilial;
      }
      getAddressInfos(item).forEach((info) => prev.ENDERECOS.add(info.enderecoCompleto));
      grouped.set(key, prev);
    });

    return [...grouped.values()]
      .map((item) => {
        const estoque = item.ESTOQUE_FILIAL === null || item.ESTOQUE_FILIAL === undefined ? null : Number(item.ESTOQUE_FILIAL);
        const diferenca = estoque === null || Number.isNaN(estoque) ? null : item.QTD_CONTADA - estoque;
        return {
          ...item,
          DIFERENCA: diferenca,
          ENDERECOS: [...item.ENDERECOS].join('; ')
        };
      })
      .filter((item) => item.DIFERENCA !== 0)
      .sort((a, b) => Math.abs(Number(b.DIFERENCA || 0)) - Math.abs(Number(a.DIFERENCA || 0)));
  }, [entries, matriculaContador]);

  const exportBaseName = useMemo(() => {
    const filial = userData?.loja_id || userData?.lojaId || userData?.loja?.id || 'sem-loja';
    const matricula = String(matriculaContador || 'sem-matricula').trim() || 'sem-matricula';
    const date = new Date();
    const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(date.getHours())}${pad(date.getMinutes())}`;
    return `pre-contagem-balanco-filial-${filial}-mat-${matricula}-${stamp}`;
  }, [matriculaContador, userData]);

  const handleEnderecoManual = (nextValue) => {
    setEndereco(normalizeAddress(nextValue));
  };

  const finalizarQuantidade = useCallback(() => {
    const normalized = String(sanitizeQuantity(quantidade));
    setQuantidade(normalized);
    focusProduto();
  }, [focusProduto, quantidade]);

  const exportarCsv = () => {
    const header = ['MATRICULA', 'CODINT', 'BARRAS', 'NOME', 'QTD', 'ENDERECO'];
    const lines = [
      header.join(';'),
      ...exportRows.map((row) => [
        csvEscape(row.MATRICULA),
        csvEscape(row.CODINT),
        csvEscape(row.BARRAS),
        csvEscape(row.NOME),
        csvEscape(row.QTD),
        csvEscape(row.ENDERECO)
      ].join(';'))
    ];
    downloadBlob(`${exportBaseName}.csv`, `\uFEFF${lines.join('\n')}`, 'text/csv;charset=utf-8;');
  };

  const exportarJson = () => {
    const payload = {
      exportedAt: new Date().toISOString(),
      filial: userData?.loja_id || userData?.lojaId || userData?.loja?.id || null,
      matriculaContador: String(matriculaContador || '').trim(),
      itens: exportRows
    };
    downloadBlob(`${exportBaseName}.json`, `${JSON.stringify(payload, null, 2)}\n`, 'application/json;charset=utf-8;');
  };

  const exportarDivergenciasCsv = () => {
    const header = ['MATRICULA', 'CODINT', 'BARRAS', 'NOME', 'QTD_CONTADA', 'ESTOQUE_FILIAL', 'DIFERENCA', 'ENDERECOS'];
    const lines = [
      header.join(';'),
      ...divergencias.map((row) => [
        csvEscape(row.MATRICULA),
        csvEscape(row.CODINT),
        csvEscape(row.BARRAS),
        csvEscape(row.NOME),
        csvEscape(row.QTD_CONTADA),
        csvEscape(row.ESTOQUE_FILIAL ?? ''),
        csvEscape(row.DIFERENCA ?? ''),
        csvEscape(row.ENDERECOS)
      ].join(';'))
    ];
    downloadBlob(`${exportBaseName}-divergencias.csv`, `\uFEFF${lines.join('\n')}`, 'text/csv;charset=utf-8;');
  };

  const abrirImportacaoCsv = () => {
    importInputRef.current?.click();
  };

  const handleImportCsv = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      setIsImporting(true);
      const text = await file.text();
      const rows = parseCsvText(text);
      if (!rows.length) {
        throw new Error('CSV vazio ou inválido.');
      }

      const imported = rows.map((row, index) => {
        const enderecosCompletos = splitAddressList(String(row.ENDERECO || row.ENDEREÇO || '').trim().toUpperCase());
        const enderecoCompleto = enderecosCompletos[0] || '';
        const enderecoBase = normalizeAddress(enderecoCompleto);
        return {
          id: `import-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
          createdAt: new Date().toISOString(),
          rawCode: String(row.BARRAS || row.CODINT || '').trim(),
          barras: String(row.BARRAS || '').trim(),
          quantidade: sanitizeQuantity(row.QTD),
          enderecoBase,
          enderecoSequencia: parseInt(String(enderecoCompleto).split('-').pop(), 10) || 1,
          enderecoCompleto: enderecosCompletos.join('; ') || enderecoCompleto || `${enderecoBase}-1`,
          enderecosCompletos,
          cdprodu: String(row.CODINT || '').trim(),
          nome: String(row.NOME || 'Produto sem descrição').trim(),
          estoqueFilial: row.ESTOQUE_FILIAL === '' || row.ESTOQUE_FILIAL === undefined ? null : Number(row.ESTOQUE_FILIAL),
          enderecoSistema: String(row.ENDERECO_SISTEMA || '').trim()
        };
      });

      setEntries((prev) => mergeEntries([...imported, ...prev]));

      const matriculaCsv = String(rows[0]?.MATRICULA || '').trim();
      if (matriculaCsv) {
        setMatriculaContador(matriculaCsv.replace(/\D+/g, ''));
      }

      mostrarFeedback('success', `${imported.length} registro(s) importados do CSV.`);
    } catch (error) {
      mostrarFeedback('error', error?.message || 'Erro ao importar CSV.');
    } finally {
      setIsImporting(false);
      focusProduto();
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const rawCode = String(produtoLido || '').trim();
    if (!rawCode) return;

    if (isAddressCode(rawCode)) {
      const enderecoLido = normalizeAddress(rawCode);
      setEndereco(enderecoLido);
      setProdutoLido('');
      mostrarFeedback('info', `Endereço alterado para ${enderecoLido}.`);
      focusProduto();
      return;
    }

    const enderecoBase = normalizeAddress(endereco);
    if (!enderecoBase) {
      mostrarFeedback('error', 'Informe ou leia um endereço antes de bipar produtos.');
      return;
    }

    const quantidadeFinal = sanitizeQuantity(quantidade);
    setQueue((prev) => ([
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        rawCode,
        quantidade: quantidadeFinal,
        enderecoBase,
        queuedAt: new Date().toISOString()
      }
    ]));
    setProdutoLido('');
    focusProduto();
  };

  const handleRemoveEntry = (entryId) => {
    setEntries((prev) => prev.filter((item) => item.id !== entryId));
  };

  const handleClearEntries = () => {
    if (!entries.length) return;
    setShowClearConfirm(true);
  };

  const confirmarLimpeza = () => {
    setEntries([]);
    setUltimoRegistro(null);
    setShowClearConfirm(false);
    mostrarFeedback('info', 'Pré-contagem local removida.');
    focusProduto();
  };

  const handleResetQuantity = () => {
    setQuantidade('1');
    focusProduto();
  };

  return (
    <div className="precontagem-page">
      <div className="precontagem-header">
        <div>
          <div className="precontagem-title">
            <i className="fas fa-barcode"></i>
            <span>Pré-Contagem Balanço</span>
          </div>
          <div className="precontagem-subtitle">
            Bipagem rápida com armazenamento local no navegador. Filial {userData?.loja_id || '-'}.
          </div>
        </div>
        <div className="precontagem-header-badges">
          <span className="precontagem-badge neutral">Local: navegador atual</span>
          <span className={`precontagem-badge precontagem-badge-status ${isProcessing ? 'warn' : 'ok'}`}>
            {isProcessing && <i className="fas fa-spinner fa-spin"></i>}
            <span>{isProcessing ? 'Processando' : 'Pronto para bipar'}</span>
          </span>
        </div>
      </div>

      <div className="precontagem-grid">
        <div className="precontagem-card precontagem-card--form">
          <div className="precontagem-card-title">
            <i className="fas fa-scanner-gun"></i>
            Leitura
          </div>

          <form onSubmit={handleSubmit} className="precontagem-form">
            <div className="precontagem-field precontagem-field--full">
              <label>Produto / Código de barras</label>
              <input
                ref={produtoInputRef}
                type="text"
                value={produtoLido}
                onChange={(event) => setProdutoLido(event.target.value)}
                placeholder="Bipe o produto ou um código de endereço"
                autoComplete="off"
                inputMode="search"
              />
            </div>

            <div className="precontagem-field">
              <label>Quantidade</label>
              <input
                ref={quantidadeInputRef}
                type="number"
                min="1"
                step="1"
                value={quantidade}
                onChange={(event) => setQuantidade(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    finalizarQuantidade();
                  }
                }}
                inputMode="numeric"
              />
            </div>

            <div className="precontagem-field precontagem-field--address">
              <label>Endereço base</label>
              <input
                ref={enderecoInputRef}
                type="text"
                value={endereco}
                onChange={(event) => handleEnderecoManual(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === 'Tab') {
                    event.preventDefault();
                    setEndereco((prev) => normalizeAddress(prev));
                    focusProduto();
                  }
                }}
                placeholder="Ex.: MD0101"
                autoComplete="off"
                className="precontagem-uppercase"
              />
            </div>

            <div className="precontagem-field precontagem-field--matricula">
              <label>Matrícula</label>
              <input
                type="text"
                value={matriculaContador}
                onChange={(event) => setMatriculaContador(String(event.target.value || '').replace(/\D+/g, ''))}
                placeholder="Quem está contando"
                inputMode="numeric"
                autoComplete="off"
              />
            </div>

            <div className="precontagem-actions">
              <button type="submit" className="precontagem-btn precontagem-btn--primary">
                <i className="fas fa-plus-circle"></i>
                Registrar
              </button>
              <button type="button" className="precontagem-btn" onClick={handleResetQuantity}>
                <i className="fas fa-rotate-left"></i>
                Qtd = 1
              </button>
            </div>
          </form>

          <div className="precontagem-inline-status">
            <div>
              <span>Próximo endereço</span>
              <strong>{proximoEndereco}</strong>
            </div>
            <div>
              <span>Fila local</span>
              <strong>{queue.length}</strong>
            </div>
            <div>
              <span>Quantidade atual</span>
              <strong>{sanitizeQuantity(quantidade)}</strong>
            </div>
          </div>

          {feedback && (
            <div className={`precontagem-feedback ${feedback.type}`}>
              <i className={`fas ${feedback.type === 'success' ? 'fa-circle-check' : feedback.type === 'error' ? 'fa-circle-exclamation' : 'fa-location-dot'}`}></i>
              <span>{feedback.message}</span>
            </div>
          )}
        </div>

        <div className="precontagem-side-column">
          <div className="precontagem-card precontagem-card--summary">
            <div className="precontagem-card-title">
              <i className="fas fa-chart-column"></i>
              Resumo
            </div>
            <div className="precontagem-summary-grid">
              <div className="precontagem-summary-item">
                <span>Registros</span>
                <strong>{resumo.totalRegistros}</strong>
              </div>
              <div className="precontagem-summary-item">
                <span>Unidades</span>
                <strong>{resumo.totalUnidades}</strong>
              </div>
              <div className="precontagem-summary-item">
                <span>Endereços</span>
                <strong>{resumo.totalEnderecos}</strong>
              </div>
            </div>
          </div>

          <div className="precontagem-card precontagem-card--last">
            <div className="precontagem-card-title">
              <i className="fas fa-clock-rotate-left"></i>
              Último registro
            </div>
            {ultimoRegistro ? (
              <div className="precontagem-last-item">
                <strong>{ultimoRegistro.cdprodu} - {ultimoRegistro.nome}</strong>
                <span>{ultimoRegistro.enderecoCompleto}</span>
                <span>Qtd: {ultimoRegistro.quantidade}</span>
                <span>Estoque filial: {ultimoRegistro.estoqueFilial ?? '-'}</span>
                <span>Endereço sistema: {ultimoRegistro.enderecoSistema || '-'}</span>
                <span>{formatDateTime(ultimoRegistro.createdAt)}</span>
              </div>
            ) : (
              <div className="precontagem-empty-note">Nenhum item registrado ainda.</div>
            )}
          </div>
        </div>
      </div>

      <div className="precontagem-card">
        <input
          ref={importInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleImportCsv}
        />
        <div className="precontagem-table-header">
          <div className="precontagem-card-title">
            <i className="fas fa-list-check"></i>
            Itens armazenados localmente
          </div>
          <div className="precontagem-table-actions">
            <button type="button" className="precontagem-btn" onClick={abrirImportacaoCsv} disabled={isImporting}>
              <i className={`fas ${isImporting ? 'fa-spinner fa-spin' : 'fa-file-import'}`}></i>
              Importar CSV
            </button>
            <button type="button" className="precontagem-btn" onClick={() => setShowDivergencias(true)} disabled={!entries.length}>
              <i className="fas fa-triangle-exclamation"></i>
              Gerar divergências
            </button>
            <button type="button" className="precontagem-btn" onClick={exportarCsv} disabled={!entries.length}>
              <i className="fas fa-file-csv"></i>
              Exportar CSV
            </button>
            <button type="button" className="precontagem-btn" onClick={exportarJson} disabled={!entries.length}>
              <i className="fas fa-file-code"></i>
              Exportar JSON
            </button>
            <button type="button" className="precontagem-btn precontagem-btn--danger" onClick={handleClearEntries}>
              <i className="fas fa-trash"></i>
              Limpar tudo
            </button>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="precontagem-empty-state">
            <i className="fas fa-box-open"></i>
            <p>Nenhuma contagem salva ainda.</p>
          </div>
        ) : (
          <div className="precontagem-table-wrap">
            <table className="precontagem-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Endereço</th>
                  <th>Qtd</th>
                  <th>Código Interno</th>
                  <th>Estoque Filial</th>
                  <th>Produto</th>
                  <th>Código Lido</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDateTime(item.createdAt)}</td>
                    <td className="mono">{item.enderecoCompleto}</td>
                    <td>{item.quantidade}</td>
                    <td className="mono">{item.cdprodu}</td>
                    <td>{item.estoqueFilial ?? '-'}</td>
                    <td>{item.nome}</td>
                    <td className="mono">{item.rawCode}</td>
                    <td>
                      <button
                        type="button"
                        className="precontagem-row-delete"
                        onClick={() => handleRemoveEntry(item.id)}
                        title="Remover registro"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDivergencias && (
        <div className="precontagem-modal-backdrop" onClick={() => setShowDivergencias(false)}>
          <div className="precontagem-modal" onClick={(event) => event.stopPropagation()}>
            <div className="precontagem-modal-header">
              <div className="precontagem-card-title" style={{ marginBottom: 0 }}>
                <i className="fas fa-triangle-exclamation"></i>
                Divergências
              </div>
              <div className="precontagem-modal-actions">
                <button type="button" className="precontagem-btn" onClick={exportarDivergenciasCsv} disabled={!divergencias.length}>
                  <i className="fas fa-file-csv"></i>
                  Exportar CSV
                </button>
                <button type="button" className="precontagem-btn" onClick={() => setShowDivergencias(false)}>
                  <i className="fas fa-xmark"></i>
                  Fechar
                </button>
              </div>
            </div>

            {!divergencias.length ? (
              <div className="precontagem-empty-state">
                <i className="fas fa-circle-check"></i>
                <p>Nenhuma divergência encontrada.</p>
              </div>
            ) : (
              <div className="precontagem-table-wrap precontagem-modal-table">
                <table className="precontagem-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Barras</th>
                      <th>Produto</th>
                      <th>Contado</th>
                      <th>Estoque</th>
                      <th>Diferença</th>
                      <th>Endereços</th>
                    </tr>
                  </thead>
                  <tbody>
                    {divergencias.map((item) => (
                      <tr key={`${item.CODINT}-${item.BARRAS}`}>
                        <td className="mono">{item.CODINT}</td>
                        <td className="mono">{item.BARRAS}</td>
                        <td>{item.NOME}</td>
                        <td>{item.QTD_CONTADA}</td>
                        <td>{item.ESTOQUE_FILIAL ?? '-'}</td>
                        <td className={Number(item.DIFERENCA || 0) > 0 ? 'precontagem-diff-up' : 'precontagem-diff-down'}>
                          {item.DIFERENCA ?? '-'}
                        </td>
                        <td>{item.ENDERECOS}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="precontagem-modal-backdrop" onClick={() => setShowClearConfirm(false)}>
          <div className="precontagem-dialog" onClick={(event) => event.stopPropagation()}>
            <div className="precontagem-dialog-icon">
              <i className="fas fa-trash"></i>
            </div>
            <div className="precontagem-dialog-title">Limpar pré-contagem</div>
            <div className="precontagem-dialog-text">
              Limpar toda a pré-contagem salva neste navegador?
            </div>
            <div className="precontagem-dialog-actions">
              <button type="button" className="precontagem-btn" onClick={() => setShowClearConfirm(false)}>
                Cancelar
              </button>
              <button type="button" className="precontagem-btn precontagem-btn--danger" onClick={confirmarLimpeza}>
                Limpar tudo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreContagemBalanco;
